import { Box, Button, Card, Typography } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CreateDestinationFormValues } from '../../organisms/create-destination/create-destination-form';

import { LoadingButton } from '@/components/atoms/loading-button/loading-button';
import { PropertyList } from '@/components/atoms/property-list';
import { PropertyListItem } from '@/components/atoms/property-list-item';
import StepTitle from '@/components/atoms/step-title';
import { ExpandableList } from '@/components/molecules/expandable-list';
import { useCreateDestination } from '@/features/destinations/hooks/mutation/use-create-destination';
import {
  CreateDestinationContext,
  CreateDestinationEvent,
} from '@/features/destinations/utils/machine/create-destination-machine';
import { useOrganizationKiosks } from '@/features/shared/hooks/query/use-organization-kiosks';
import { usePermissions } from '@/permissions/can';

interface Props {
  context: CreateDestinationContext;
  send: (event: CreateDestinationEvent) => void;
}

export const Confirmation = ({ context, send }: Props) => {
  const { t } = useTranslation(['common', 'createDestination', 'destination']);
  const [expandedHosts, setExpandedHosts] = useState(false);
  const permissions = usePermissions();
  const cannotManageDestinationHosts = permissions.cannot('manageDestinations', 'Hosts');

  const navigate = useNavigate({ from: '/destinations/create' });

  const { data: organizationKiosks } = useOrganizationKiosks();
  const hasOrganizationKiosk = organizationKiosks && organizationKiosks.length > 0;

  const { control, handleSubmit } = useFormContext<CreateDestinationFormValues>();

  const [
    name,
    floor,
    description,
    maxCapacity,
    accessAfterVisitInMinutes,
    accessBeforeVisitInMinutes,
    translations,
    directionTips,
    hosts,
    devices,
    allowedKiosks,
    defaultHostId,
    addHostStepEnabled,
    hostObligatory,
    hostConfirmationObligatory,
    hostConfirmationInEmail,
    deviceConfirmation,
    kioskVisitHours,
  ] = useWatch({
    control,
    name: [
      'name',
      'floor',
      'description',
      'maxCapacity',
      'accessAfterVisitInMinutes',
      'accessBeforeVisitInMinutes',
      'translations',
      'directionTips',
      'hosts',
      'devices',
      'allowedKiosks',
      'defaultHostId',
      'addHostStepEnabled',
      'hostObligatory',
      'hostConfirmationObligatory',
      'hostConfirmationInEmail',
      'deviceConfirmation',
      'kioskVisitHours',
    ],
  });
  const isKioskEnabled = (allowedKiosks?.length || 0) > 0;
  const isDefaultHostAssigned = defaultHostId !== null;
  const effectiveAddHostStepEnabled = isDefaultHostAssigned ? false : addHostStepEnabled;
  const effectiveHostConfirmationObligatory = hostConfirmationObligatory;
  const effectiveHostConfirmationInEmail = hostConfirmationInEmail;
  const hasAssignedDevices = (devices?.length || 0) > 0;
  const effectiveDeviceConfirmation = deviceConfirmation && isKioskEnabled && hasAssignedDevices;
  const defaultHost = (hosts || []).find(host => host.id === defaultHostId);
  const defaultHostName = defaultHost?.name || t('destination:kiosk.defaultHostNotSet');
  const selectedKioskNames = (allowedKiosks || []).map(({ name }) => name);

  const { mutate, isPending } = useCreateDestination(() => navigate({ to: '/destinations' }));

  const directionTipsByLanguage = {
    pl: (directionTips || []).map(tip => tip.pl.trim()).filter(Boolean),
    en: (directionTips || []).map(tip => tip.en.trim()).filter(Boolean),
    ua: (directionTips || []).map(tip => tip.ua.trim()).filter(Boolean),
  };

  const onSubmit = (data: CreateDestinationFormValues) => {
    const { hosts, devices, allowedKiosks, directionTips, ...rest } = data;
    const isDefaultHostAssigned = rest.defaultHostId !== null;

    const formattedDirectionTips = (
      Object.entries({
        pl: directionTips.map(tip => tip.pl.trim()).filter(Boolean),
        en: directionTips.map(tip => tip.en.trim()).filter(Boolean),
        ua: directionTips.map(tip => tip.ua.trim()).filter(Boolean),
      }) as ['pl' | 'en' | 'ua', string[]][]
    )
      .filter(([, tips]) => tips.length > 0)
      .map(([language, tips]) => ({
        language,
        tips,
      }));

    const payload = {
      ...rest,
      addHostStepEnabled: isDefaultHostAssigned ? false : rest.addHostStepEnabled,
      hostObligatory: isDefaultHostAssigned ? false : rest.hostObligatory,
      hostConfirmationObligatory: rest.hostConfirmationObligatory,
      hostConfirmationInEmail: rest.hostConfirmationInEmail,
      deviceConfirmation: rest.deviceConfirmation,
      defaultHostId: rest.defaultHostId ?? undefined,
      hostIds: cannotManageDestinationHosts ? undefined : hosts.map(host => host.id),
      deviceIds: devices.map(device => device.id),
      allowedKioskIds: allowedKiosks.map(({ id }) => id),
      kioskVisitHours: allowedKiosks.length > 0 ? rest.kioskVisitHours : undefined,
      directionTips: formattedDirectionTips.length > 0 ? formattedDirectionTips : undefined,
      translations: {
        pl: rest.translations?.pl || undefined,
        en: rest.translations?.en || undefined,
        ua: rest.translations?.ua || undefined,
      },
    };

    mutate(payload);
  };

  return (
    <div>
      <StepTitle
        title={t('common:stepTitle', {
          step: context.step,
          title: t(context.content),
        })}
      />
      <Card variant="outlined">
        <PropertyList>
          <PropertyListItem
            divider
            width={300}
            label={t('createDestination:form.name')}
            value={name}
          />
          <PropertyListItem
            divider
            width={300}
            label={t('createDestination:form.floor')}
            value={floor}
          />
          <PropertyListItem
            divider
            width={300}
            label={t('createDestination:form.description')}
            value={description}
          />
          <PropertyListItem
            width={300}
            divider
            label={t('createDestination:form.maxCapacity')}
            value={maxCapacity?.toString()}
          />
          <PropertyListItem
            width={300}
            divider
            label={t('createDestination:form.accessAfter')}
            value={accessAfterVisitInMinutes?.toString()}
          />
          <PropertyListItem
            width={300}
            divider
            label={t('createDestination:form.accessBefore')}
            value={accessBeforeVisitInMinutes?.toString()}
          />

          {(translations?.pl || translations?.en || translations?.ua) && (
            <>
              {translations?.pl && (
                <PropertyListItem
                  divider
                  width={300}
                  label={t('destination:translations.polish')}
                  value={translations.pl}
                />
              )}
              {translations?.en && (
                <PropertyListItem
                  divider
                  width={300}
                  label={t('destination:translations.english')}
                  value={translations.en}
                />
              )}
              {translations?.ua && (
                <PropertyListItem
                  divider
                  width={300}
                  label={t('destination:translations.ukrainian')}
                  value={translations.ua}
                />
              )}
            </>
          )}

          {(directionTipsByLanguage.pl.length > 0 ||
            directionTipsByLanguage.en.length > 0 ||
            directionTipsByLanguage.ua.length > 0) && (
            <>
              {directionTipsByLanguage.pl.length > 0 && (
                <PropertyListItem
                  divider
                  width={300}
                  label={t('createDestination:form.directionTipsPolish')}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {directionTipsByLanguage.pl.map((tip, index) => (
                      <Typography color="textSecondary" variant="body2" key={index}>
                        {tip}
                        {index < directionTipsByLanguage.pl.length - 1 && <br />}
                      </Typography>
                    ))}
                  </Box>
                </PropertyListItem>
              )}
              {directionTipsByLanguage.en.length > 0 && (
                <PropertyListItem
                  divider
                  width={300}
                  label={t('createDestination:form.directionTipsEnglish')}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {directionTipsByLanguage.en.map((tip, index) => (
                      <Typography color="textSecondary" variant="body2" key={index}>
                        {tip}
                        {index < directionTipsByLanguage.en.length - 1 && <br />}
                      </Typography>
                    ))}
                  </Box>
                </PropertyListItem>
              )}
              {directionTipsByLanguage.ua.length > 0 && (
                <PropertyListItem
                  divider
                  width={300}
                  label={t('createDestination:form.directionTipsUkrainian')}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {directionTipsByLanguage.ua.map((tip, index) => (
                      <Typography color="textSecondary" variant="body2" key={index}>
                        {tip}
                        {index < directionTipsByLanguage.ua.length - 1 && <br />}
                      </Typography>
                    ))}
                  </Box>
                </PropertyListItem>
              )}
            </>
          )}

          {hosts && hosts.length > 0 && (
            <PropertyListItem divider width={300} label={t('createDestination:form.hosts')}>
              <ExpandableList
                items={hosts || []}
                expanded={expandedHosts}
                setExpanded={setExpandedHosts}
                renderItem={host => host.name}
              />
            </PropertyListItem>
          )}

          {devices && devices.length > 0 && (
            <PropertyListItem
              divider
              width={300}
              label={t('createDestination:form.receptions')}
              value={devices.map(device => device.name).join(', ')}
            />
          )}

          {hasOrganizationKiosk && (
            <>
              <PropertyListItem
                divider
                width={300}
                label={t('destination:kiosk.allowedKiosks')}
                value={
                  selectedKioskNames.length > 0
                    ? selectedKioskNames.join(', ')
                    : t('destination:kiosk.noKiosksSelected')
                }
              />
              <PropertyListItem
                divider
                width={300}
                label={t('destination:kiosk.defaultHost')}
                value={defaultHostName}
              />
            </>
          )}
          {isKioskEnabled && hasOrganizationKiosk && (
            <>
              <PropertyListItem
                divider
                width={300}
                label={t('destination:kiosk.addHostStepEnabled')}
                value={effectiveAddHostStepEnabled ? t('common:yes') : t('common:no')}
              />
              {effectiveAddHostStepEnabled && (
                <PropertyListItem
                  divider
                  width={300}
                  label={t('destination:kiosk.hostObligatory')}
                  value={hostObligatory ? t('common:yes') : t('common:no')}
                />
              )}
              {(effectiveAddHostStepEnabled || isDefaultHostAssigned) && (
                <PropertyListItem
                  divider
                  width={300}
                  label={t('destination:kiosk.hostConfirmationObligatory')}
                  value={effectiveHostConfirmationObligatory ? t('common:yes') : t('common:no')}
                />
              )}
              {(effectiveAddHostStepEnabled || isDefaultHostAssigned) && (
                <PropertyListItem
                  divider
                  width={300}
                  label={t('destination:kiosk.hostConfirmationInEmail')}
                  value={effectiveHostConfirmationInEmail ? t('common:yes') : t('common:no')}
                />
              )}
              <PropertyListItem
                divider
                width={300}
                label={t('destination:kiosk.deviceConfirmation')}
                value={effectiveDeviceConfirmation ? t('common:yes') : t('common:no')}
              />
              <PropertyListItem
                width={300}
                divider
                label={t('destination:kiosk.kioskVisitHours')}
                value={`${kioskVisitHours}h`}
              />
            </>
          )}
        </PropertyList>
      </Card>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 3,
        }}>
        <Button
          color="primary"
          onClick={() => send({ type: 'BACK' })}
          size="large"
          sx={{ mr: 2 }}
          type="button"
          variant="text">
          {t('common:button.back')}
        </Button>
        <LoadingButton
          loading={isPending}
          onClick={handleSubmit(onSubmit)}
          color="primary"
          size="large"
          type="submit"
          variant="contained">
          {t('common:button.confirm')}
        </LoadingButton>
      </Box>
    </div>
  );
};
